import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  COLOR_FAMILIES,
  suggestColorFamily,
  type CatalogOptionDisplay,
  type CatalogOptionDto,
  type CatalogOptionValueDto,
  type CatalogOptionValueInput,
  type ColorFamilyCode,
} from '@ecom/contracts';
import { useOptionsStore, type FamilyFilter } from '@/stores/options.store';
import { useUiStore, type AdminLocale } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { thousandthsToUnits, unitsToThousandths } from '@/lib/price-input';
import { AlertIcon, CogIcon, PlusIcon, SearchIcon, SparkleIcon, SwatchIcon } from '@/components/icons';
import {
  ListCard,
  ListToolbar,
  LoadMore,
  Page,
  Segmented,
  Sheet,
  Table,
  TableScroll,
  Tabs,
  Td,
  Th,
  Tr,
} from '@/components/page';
import {
  Alert,
  Button,
  EmptyState,
  Field,
  Input,
  Pill,
  Select,
  Skeleton,
  Switch,
  TableSkeleton,
  cx,
} from '@/components/ui';

/**
 * The options library (ADR-052, docs/01-Business/Merchandising.md R1).
 *
 * Colour, "sold as", size — each option and its values named once for the
 * whole store, the way Shopify's colour library and Salla's options work.
 * Products pick their values from here, so a colour renamed or recoloured here
 * is renamed on every product that uses it (except one that calls it something
 * of its own).
 *
 * One tab per option, remembered in the address (`?option=color`). A colour
 * option reads as a wall of swatches with a family filter — families are what
 * the storefront's colour filter offers, so "Indigo" and "Midnight" are one
 * entry, Blue — and "suggest families" files the colours that have none yet.
 * Any other option is a table: what each value holds, whether it is a sample.
 * Values are searched and paged on the server. Creating or editing anything
 * opens in a side panel, so the library stays in view.
 */

const DISPLAYS: CatalogOptionDisplay[] = ['SWATCH', 'BUTTONS', 'LIST'];
const FAMILY_BY_CODE = new Map(COLOR_FAMILIES.map((family) => [family.code, family]));

export function OptionsLibraryPage() {
  const { options, optionsLoading, optionsLoaded, error, loadOptions } = useOptionsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [sheet, setSheet] = useState<'new' | 'settings' | null>(null);

  useEffect(() => {
    void loadOptions();
  }, [loadOptions]);

  const selected = options.find((option) => option.code === params.get('option')) ?? options[0] ?? null;

  const choose = (code: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('option', code);
        return next;
      },
      { replace: true },
    );

  const newOption = (
    <Button onClick={() => setSheet('new')}>
      <PlusIcon className="size-4" />
      {t('optNewOption')}
    </Button>
  );

  return (
    <Page
      title={t('optionsLibrary')}
      subtitle={t('optionsLibrarySubtitle')}
      actions={newOption}
      tabs={
        options.length > 0 && selected ? (
          <Tabs
            label={t('optionsLibrary')}
            value={selected.code}
            onChange={choose}
            items={options.map((option) => ({
              id: option.code,
              label: optionName(option, locale),
              count: count(option.valueCount, locale),
              muted: !option.isActive,
            }))}
          />
        ) : undefined
      }
      fill
    >
      {selected ? (
        // Keyed by the option, so its search and filters start afresh on each tab.
        <ValuesPanel key={selected.id} option={selected} onSettings={() => setSheet('settings')} />
      ) : (
        <ListCard>
          {error ? (
            <div className="px-4 pt-4">
              <Alert>{error}</Alert>
            </div>
          ) : null}
          {optionsLoading || !optionsLoaded ? (
            <TableSkeleton rows={5} columns={4} />
          ) : (
            <EmptyState
              title={t('optNoOptions')}
              body={t('optNoOptionsHint')}
              icon={<SwatchIcon />}
              action={newOption}
            />
          )}
        </ListCard>
      )}

      {sheet === 'new' ? (
        <OptionSheet
          option={null}
          onDone={(created) => {
            setSheet(null);
            if (created) choose(created.code);
          }}
        />
      ) : null}
      {sheet === 'settings' && selected ? (
        <OptionSheet key={selected.id} option={selected} onDone={() => setSheet(null)} />
      ) : null}
    </Page>
  );
}

// ─── One option's values ─────────────────────────────────────────────────────

function ValuesPanel({ option, onSettings }: { option: CatalogOptionDto; onSettings: () => void }) {
  const {
    optionId,
    values,
    nextCursor,
    search,
    family,
    loading,
    loadingMore,
    error,
    summary,
    loadValues,
    loadMoreValues,
    loadSummary,
  } = useOptionsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [query, setQuery] = useState('');
  const [needsNameOnly, setNeedsNameOnly] = useState(false);
  const [editing, setEditing] = useState<CatalogOptionValueDto | 'new' | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  const isSwatch = option.display === 'SWATCH';
  const current = optionId === option.id;

  useEffect(() => {
    void loadValues(option.id, { search: '', family: '' });
    void loadSummary(option.id);
  }, [option.id, loadValues, loadSummary]);

  useEffect(() => {
    if (!current || query === search) return;
    const timer = setTimeout(() => void loadValues(option.id, { search: query }), 350);
    return () => clearTimeout(timer);
  }, [query, search, current, option.id, loadValues]);

  // An option switched from swatches to buttons has no families to filter by.
  useEffect(() => {
    if (current && !isSwatch && family) void loadValues(option.id, { family: '' });
  }, [current, isSwatch, family, option.id, loadValues]);

  const all = summary?.optionId === option.id ? summary.values : [];
  const unnamed = all.filter((value) => value.needsName);
  const unfiled = all.filter((value) => !value.family && value.swatchHex);

  // The unnamed are read from the whole library, not the page in view: after
  // an import they sit at its end, where nobody scrolls. Naming the last one
  // puts the library back.
  const showingUnnamed = needsNameOnly && unnamed.length > 0;
  const shown = showingUnnamed ? unnamed : current ? values : [];
  const filtered = Boolean(search || family);

  const chooseFamily = (next: FamilyFilter) => {
    setNeedsNameOnly(false);
    void loadValues(option.id, { family: next });
  };

  const newValue = (
    <Button onClick={() => setEditing('new')}>
      <PlusIcon className="size-4" />
      {t('optNewValue')}
    </Button>
  );

  return (
    <ListCard>
      <ListToolbar>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-72">
            <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
              <SearchIcon className="size-4" />
            </span>
            <Input
              type="search"
              placeholder={t('optSearchValues')}
              aria-label={t('optSearchValues')}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setNeedsNameOnly(false);
              }}
              className="ps-9"
            />
          </div>

          {unnamed.length > 0 ? (
            <button
              type="button"
              aria-pressed={showingUnnamed}
              onClick={() => setNeedsNameOnly((on) => !on)}
              className={cx(
                'inline-flex min-h-8 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors',
                showingUnnamed ? 'bg-warning text-warning-ink' : 'bg-warning/15 text-warning hover:bg-warning/25',
              )}
            >
              <AlertIcon className="size-3.5" />
              <span data-numeric>{t('optNeedsNameCount', { count: count(unnamed.length, locale) })}</span>
            </button>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 sm:ms-auto">
            <Button variant="ghost" onClick={onSettings}>
              <CogIcon className="size-4" />
              {t('optSettings')}
            </Button>
            {isSwatch ? (
              <Button variant="secondary" onClick={() => setSuggesting(true)}>
                <SparkleIcon className="size-4" />
                {t('optSuggestFamilies')}
                {unfiled.length > 0 ? (
                  <span data-numeric className="rounded-full bg-brand-50 px-1.5 py-px text-[11px] leading-4 text-brand-700">
                    {count(unfiled.length, locale)}
                  </span>
                ) : null}
              </Button>
            ) : null}
            {newValue}
          </div>
        </div>

        {isSwatch ? (
          <div role="radiogroup" aria-label={t('optFamily')} className="flex flex-wrap gap-1.5">
            <Chip active={!showingUnnamed && family === ''} onClick={() => chooseFamily('')}>
              {t('optAllFamilies')}
            </Chip>
            {COLOR_FAMILIES.map((entry) => (
              <Chip
                key={entry.code}
                active={!showingUnnamed && family === entry.code}
                onClick={() => chooseFamily(entry.code)}
              >
                <Dot hex={entry.hex} />
                {entry[locale]}
              </Chip>
            ))}
          </div>
        ) : null}
      </ListToolbar>

      {error ? (
        <div className="px-4 pt-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {(loading || !current) && shown.length === 0 ? (
        isSwatch ? (
          <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} className="h-44 rounded-card" />
            ))}
          </div>
        ) : (
          <TableSkeleton rows={6} columns={6} />
        )
      ) : shown.length === 0 ? (
        filtered ? (
          <EmptyState title={t('optNoMatches')} body={t('optNoMatchesHint')} icon={<SearchIcon />} />
        ) : (
          <EmptyState title={t('optNoValues')} body={t('optNoValuesHint')} icon={<SwatchIcon />} action={newValue} />
        )
      ) : isSwatch ? (
        <TableScroll>
          <ul className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {shown.map((value) => (
              <li key={value.id}>
                <SwatchCard value={value} onOpen={() => setEditing(value)} />
              </li>
            ))}
          </ul>
        </TableScroll>
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>{t('nameArabic')}</Th>
                <Th>{t('nameEnglish')}</Th>
                <Th>{t('colourCode')}</Th>
                <Th align="end">{t('optHolds')}</Th>
                <Th>{t('optSample')}</Th>
                <Th align="end">{t('products')}</Th>
                <Th>{t('optStatus')}</Th>
              </tr>
            </thead>
            <tbody>
              {shown.map((value) => (
                <Tr key={value.id} onClick={() => setEditing(value)}>
                  <Td>
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink">{value.translations?.ar || t('dash')}</span>
                      {value.needsName ? <Pill tone="warning">{t('optNeedsName')}</Pill> : null}
                    </span>
                  </Td>
                  <Td className="text-ink">{value.translations?.en || t('dash')}</Td>
                  <Td>
                    <span dir="ltr" className="font-mono text-xs text-ink-muted">
                      {value.code}
                    </span>
                  </Td>
                  <Td align="end">
                    {value.measureQuantity !== null ? (
                      <span data-numeric dir="ltr" className="text-ink">
                        {thousandthsToUnits(value.measureQuantity)}
                      </span>
                    ) : (
                      <span className="text-ink-faint">{t('dash')}</span>
                    )}
                  </Td>
                  <Td>
                    {value.isSample ? (
                      <Pill tone="info">{t('optSample')}</Pill>
                    ) : (
                      <span className="text-ink-faint">{t('dash')}</span>
                    )}
                  </Td>
                  <Td align="end">
                    <span data-numeric className="text-ink">
                      {count(value.productCount, locale)}
                    </span>
                  </Td>
                  <Td>
                    {value.isActive ? <Pill tone="success">{t('optActive')}</Pill> : <Pill>{t('inactive')}</Pill>}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}

      <LoadMore
        visible={!showingUnnamed && current && Boolean(nextCursor)}
        loading={loadingMore}
        onClick={() => void loadMoreValues()}
      />

      {editing ? (
        <ValueSheet
          // Keyed by the value, so opening another one starts its form afresh.
          key={editing === 'new' ? 'new' : editing.id}
          option={option}
          value={editing === 'new' ? null : editing}
          onDone={() => setEditing(null)}
        />
      ) : null}
      {suggesting ? <SuggestFamiliesSheet option={option} onDone={() => setSuggesting(false)} /> : null}
    </ListCard>
  );
}

function SwatchCard({ value, onOpen }: { value: CatalogOptionValueDto; onOpen: () => void }) {
  const t = useT();

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex h-full w-full flex-col gap-3 rounded-card border border-line bg-surface p-4 text-start transition-all hover:border-brand-300 hover:shadow-raised"
    >
      <span className="flex w-full items-start justify-between gap-3">
        <SwatchCircle hex={value.swatchHex} className="size-14" />
        <span className="flex flex-wrap justify-end gap-1.5">
          {value.needsName ? <Pill tone="warning">{t('optNeedsName')}</Pill> : null}
          {!value.isActive ? <Pill>{t('inactive')}</Pill> : null}
        </span>
      </span>

      <span className="block w-full min-w-0">
        <span className="block truncate font-semibold text-ink">{value.translations?.ar || t('dash')}</span>
        <span className="block truncate text-sm text-ink-muted">{value.translations?.en || t('dash')}</span>
        <span className="mt-1 block truncate text-xs text-ink-faint">
          <span dir="ltr" className="font-mono">
            {value.code}
          </span>
        </span>
      </span>

      <span className="mt-auto flex w-full flex-wrap items-center justify-between gap-2">
        <FamilyPill code={value.family} />
        <UsedBy productCount={value.productCount} />
      </span>
    </button>
  );
}

function FamilyPill({ code }: { code: ColorFamilyCode | null }) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const family = code ? FAMILY_BY_CODE.get(code) : undefined;

  if (!family) return <span className="text-xs text-ink-faint">{t('optNoFamily')}</span>;
  return (
    <Pill>
      <Dot hex={family.hex} />
      {family[locale]}
    </Pill>
  );
}

function UsedBy({ productCount }: { productCount: number }) {
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const text =
    productCount === 0
      ? t('optUsedByNone')
      : productCount === 1
        ? t('optUsedByOne')
        : t('optUsedBy', { count: count(productCount, locale) });
  return (
    <span data-numeric className="text-xs text-ink-muted">
      {text}
    </span>
  );
}

// ─── Sheets ──────────────────────────────────────────────────────────────────

interface OptionDraft {
  ar: string;
  en: string;
  code: string;
  /** Typed by hand: the English name stops writing it. */
  codeTouched: boolean;
  display: CatalogOptionDisplay;
  isFilterable: boolean;
  isActive: boolean;
}

/** A new option, or one option's settings: its names, how it is picked, whether it filters. */
function OptionSheet({
  option,
  onDone,
}: {
  option: CatalogOptionDto | null;
  onDone: (created?: CatalogOptionDto) => void;
}) {
  const { createOption, updateOption, saving, saveError, clearSaveError } = useOptionsStore();
  const t = useT();
  const [draft, setDraft] = useState<OptionDraft>(() => ({
    ar: option?.translations?.ar ?? '',
    en: option?.translations?.en ?? '',
    code: option?.code ?? '',
    codeTouched: false,
    display: option?.display ?? 'BUTTONS',
    isFilterable: option?.isFilterable ?? false,
    isActive: option?.isActive ?? true,
  }));
  const set = (patch: Partial<OptionDraft>) => setDraft((current) => ({ ...current, ...patch }));

  useEffect(() => clearSaveError(), [clearSaveError]);

  const code = toCode(draft.code, 40);
  const codeMissing = option === null && draft.en.trim() !== '' && code === '';
  const canSave = !saving && draft.en.trim() !== '' && (option !== null || code !== '');

  const save = async () => {
    const translations = { en: draft.en.trim(), ar: draft.ar.trim() || null };
    if (option) {
      const ok = await updateOption(option.id, {
        translations,
        display: draft.display,
        isFilterable: draft.isFilterable,
        isActive: draft.isActive,
      });
      if (ok) onDone();
      return;
    }
    const created = await createOption({
      code,
      translations,
      display: draft.display,
      isFilterable: draft.isFilterable,
    });
    if (created) onDone(created);
  };

  return (
    <Sheet
      open
      title={option ? t('optSettings') : t('optNewOption')}
      subtitle={option ? t('optSettingsHint') : t('optNewOptionHint')}
      onClose={() => onDone()}
      footer={
        <>
          <Button variant="secondary" onClick={() => onDone()} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button disabled={!canSave} onClick={() => void save()}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {saveError ? <Alert>{saveError}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('nameArabic')} htmlFor="option-ar">
            <Input id="option-ar" dir="rtl" value={draft.ar} onChange={(event) => set({ ar: event.target.value })} />
          </Field>
          <Field label={t('nameEnglish')} htmlFor="option-en">
            <Input
              id="option-en"
              dir="ltr"
              value={draft.en}
              onChange={(event) =>
                set({
                  en: event.target.value,
                  ...(option || draft.codeTouched ? {} : { code: toCode(event.target.value, 40) }),
                })
              }
            />
          </Field>
        </div>

        <Field
          label={t('colourCode')}
          htmlFor="option-code"
          hint={option ? t('optCodeFixed') : t('optOptionCodeHint')}
          error={codeMissing ? t('optCodeInvalid') : undefined}
        >
          <Input
            id="option-code"
            dir="ltr"
            className="font-mono"
            value={draft.code}
            // A code is identity: variants key on it, so it is set once.
            disabled={option !== null}
            invalid={codeMissing}
            onChange={(event) => set({ code: typingCode(event.target.value, 40), codeTouched: event.target.value !== '' })}
          />
        </Field>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink">{t('optDisplay')}</span>
          <div>
            <Segmented
              label={t('optDisplay')}
              value={draft.display}
              onChange={(display) => set({ display })}
              items={DISPLAYS.map((display) => ({ id: display, label: t(`optDisplay_${display}`) }))}
            />
          </div>
          <p className="text-xs text-ink-muted">{t('optDisplayHint')}</p>
        </div>

        <SwitchRow
          label={t('filterable')}
          hint={t('filterableHint')}
          checked={draft.isFilterable}
          onChange={(isFilterable) => set({ isFilterable })}
        />
        {option ? (
          <SwitchRow label={t('optActive')} checked={draft.isActive} onChange={(isActive) => set({ isActive })} />
        ) : null}
      </div>
    </Sheet>
  );
}

interface ValueDraft {
  ar: string;
  en: string;
  code: string;
  codeTouched: boolean;
  hex: string;
  family: ColorFamilyCode | '';
  /** What a new variant holds, as typed — "10", "0.5". */
  holds: string;
  isSample: boolean;
  isActive: boolean;
}

/** A new value, or one value: its names, and a colour's swatch and family or another option's defaults. */
function ValueSheet({
  option,
  value,
  onDone,
}: {
  option: CatalogOptionDto;
  value: CatalogOptionValueDto | null;
  onDone: () => void;
}) {
  const { saveValue, saving, saveError, clearSaveError } = useOptionsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [draft, setDraft] = useState<ValueDraft>(() => ({
    ar: value?.translations?.ar ?? '',
    en: value?.translations?.en ?? '',
    code: value?.code ?? '',
    codeTouched: false,
    hex: value?.swatchHex ?? '',
    family: value?.family ?? '',
    holds: thousandthsToUnits(value?.measureQuantity),
    isSample: value?.isSample ?? false,
    isActive: value?.isActive ?? true,
  }));
  const set = (patch: Partial<ValueDraft>) => setDraft((current) => ({ ...current, ...patch }));

  useEffect(() => clearSaveError(), [clearSaveError]);

  const isSwatch = option.display === 'SWATCH';
  const code = toCode(draft.code, 80);
  const codeMissing = value === null && draft.en.trim() !== '' && code === '';
  const hex = normalizeHex(draft.hex);
  const hexInvalid = isSwatch && draft.hex.trim() !== '' && hex === null;
  const holds = draft.holds.trim() === '' ? null : unitsToThousandths(draft.holds);
  const holdsInvalid = !isSwatch && draft.holds.trim() !== '' && holds === null;
  const suggestion = suggestColorFamily(hex);
  const canSave =
    !saving && draft.en.trim() !== '' && (value !== null || code !== '') && !hexInvalid && !holdsInvalid;

  const save = async () => {
    const body: CatalogOptionValueInput = {
      ...(value ? {} : { code }),
      translations: { en: draft.en.trim(), ar: draft.ar.trim() || null },
      ...(isSwatch
        ? { swatchHex: hex, family: draft.family || null }
        : { measureQuantity: holds, isSample: draft.isSample }),
      isActive: draft.isActive,
    };
    if (await saveValue(option.id, value?.id ?? null, body)) onDone();
  };

  return (
    <Sheet
      open
      title={value ? t('optEditValue') : t('optNewValue')}
      subtitle={value ? t('optValueSheetHint') : t('optNewValueHint')}
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button disabled={!canSave} onClick={() => void save()}>
            {saving ? t('saving') : t('save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {saveError ? <Alert>{saveError}</Alert> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('nameArabic')} htmlFor="value-ar">
            <Input id="value-ar" dir="rtl" value={draft.ar} onChange={(event) => set({ ar: event.target.value })} />
          </Field>
          <Field label={t('nameEnglish')} htmlFor="value-en">
            <Input
              id="value-en"
              dir="ltr"
              value={draft.en}
              onChange={(event) =>
                set({
                  en: event.target.value,
                  ...(value || draft.codeTouched ? {} : { code: toCode(event.target.value, 80) }),
                })
              }
            />
          </Field>
        </div>

        <Field
          label={t('colourCode')}
          htmlFor="value-code"
          hint={value ? t('optCodeFixed') : t('optValueCodeHint')}
          error={codeMissing ? t('optCodeInvalid') : undefined}
        >
          <Input
            id="value-code"
            dir="ltr"
            className="font-mono"
            value={draft.code}
            // Variants and carts hold the code, so it is set once.
            disabled={value !== null}
            invalid={codeMissing}
            onChange={(event) => set({ code: typingCode(event.target.value, 80), codeTouched: event.target.value !== '' })}
          />
        </Field>

        {isSwatch ? (
          <>
            <Field
              label={t('swatch')}
              htmlFor="value-hex"
              error={hexInvalid ? t('optSwatchInvalid') : undefined}
            >
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label={t('optSwatchPick')}
                  value={hex ?? '#ffffff'}
                  onChange={(event) => set({ hex: event.target.value })}
                  className="h-10 w-12 shrink-0 cursor-pointer rounded-control border border-line bg-surface p-1"
                />
                <Input
                  id="value-hex"
                  dir="ltr"
                  placeholder="#796727"
                  maxLength={7}
                  value={draft.hex}
                  invalid={hexInvalid}
                  onChange={(event) => set({ hex: event.target.value })}
                  className="w-32 font-mono"
                />
              </div>
            </Field>

            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-ink">{t('optFamily')}</span>
                {suggestion && suggestion !== draft.family ? (
                  // A suggestion only: "rose gold" is pink to a hue wheel and gold to a buyer.
                  <button
                    type="button"
                    onClick={() => set({ family: suggestion })}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
                  >
                    <SparkleIcon className="size-3.5" />
                    {t('optSuggestFrom', { family: familyName(suggestion, locale) })}
                  </button>
                ) : null}
              </div>
              <div role="radiogroup" aria-label={t('optFamily')} className="flex flex-wrap gap-1.5">
                <Chip active={draft.family === ''} onClick={() => set({ family: '' })}>
                  {t('optNoFamily')}
                </Chip>
                {COLOR_FAMILIES.map((entry) => (
                  <Chip key={entry.code} active={draft.family === entry.code} onClick={() => set({ family: entry.code })}>
                    <Dot hex={entry.hex} />
                    {entry[locale]}
                  </Chip>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            <Field
              label={t('optHolds')}
              htmlFor="value-holds"
              hint={t('optHoldsHint')}
              error={holdsInvalid ? t('optHoldsInvalid') : undefined}
            >
              <Input
                id="value-holds"
                dir="ltr"
                inputMode="decimal"
                placeholder="10"
                value={draft.holds}
                invalid={holdsInvalid}
                onChange={(event) => set({ holds: event.target.value })}
                className="w-32"
              />
            </Field>
            <SwitchRow
              label={t('optSample')}
              hint={t('optSampleHint')}
              checked={draft.isSample}
              onChange={(isSample) => set({ isSample })}
            />
          </>
        )}

        <SwitchRow
          label={t('optActive')}
          hint={t('optValueActiveHint')}
          checked={draft.isActive}
          onChange={(isActive) => set({ isActive })}
        />
      </div>
    </Sheet>
  );
}

/**
 * Files the colours that have no family, each under the one its swatch
 * suggests — reviewed first, because a hue wheel and a fabric buyer disagree
 * about "rose gold". This is how a store's colour filter goes from one entry
 * per product to a dozen families.
 */
function SuggestFamiliesSheet({ option, onDone }: { option: CatalogOptionDto; onDone: () => void }) {
  const { summary, loadSummary, fileFamilies, saving, saveError, filing, clearSaveError } = useOptionsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [choices, setChoices] = useState<Record<string, { include: boolean; family: ColorFamilyCode | '' }>>({});

  useEffect(() => {
    clearSaveError();
    void loadSummary(option.id);
  }, [option.id, clearSaveError, loadSummary]);

  const unfiled = summary?.optionId === option.id ? summary.values.filter((value) => !value.family) : null;
  const candidates = (unfiled ?? []).filter((value) => value.swatchHex);
  const skipped = (unfiled?.length ?? 0) - candidates.length;

  const choiceOf = (value: CatalogOptionValueDto) => {
    const suggested = suggestColorFamily(value.swatchHex);
    return choices[value.id] ?? { include: suggested !== null, family: suggested ?? '' };
  };
  const choose = (value: CatalogOptionValueDto, patch: Partial<{ include: boolean; family: ColorFamilyCode | '' }>) =>
    setChoices((current) => ({ ...current, [value.id]: { ...choiceOf(value), ...patch } }));

  const entries = candidates.flatMap((value) => {
    const choice = choiceOf(value);
    return choice.include && choice.family ? [{ valueId: value.id, family: choice.family }] : [];
  });

  return (
    <Sheet
      open
      size="lg"
      title={t('optSuggestFamilies')}
      subtitle={t('optSuggestFamiliesHint')}
      onClose={onDone}
      footer={
        <>
          <Button variant="secondary" onClick={onDone} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            disabled={saving || entries.length === 0}
            onClick={async () => {
              if (await fileFamilies(option.id, entries)) onDone();
            }}
          >
            {filing
              ? t('optApplying', { done: count(filing.done, locale), total: count(filing.total, locale) })
              : t('optApplyCount', { count: count(entries.length, locale) })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {saveError ? <Alert>{saveError}</Alert> : null}

        {unfiled === null ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : candidates.length === 0 ? (
          <EmptyState title={t('optSuggestNone')} icon={<SwatchIcon />} />
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {candidates.map((value) => {
              const choice = choiceOf(value);
              const name = valueName(value, locale);
              const chosen = choice.family ? FAMILY_BY_CODE.get(choice.family) : undefined;
              return (
                <li key={value.id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <input
                    type="checkbox"
                    aria-label={t('optInclude', { name })}
                    checked={choice.include}
                    onChange={(event) => choose(value, { include: event.target.checked })}
                    className="size-4 shrink-0"
                  />
                  <SwatchCircle hex={value.swatchHex} className="size-9" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{name}</span>
                    <span className="block truncate text-xs text-ink-faint">
                      <span dir="ltr" className="font-mono">
                        {value.code} · {value.swatchHex}
                      </span>
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <Dot hex={chosen?.hex ?? null} />
                    <Select
                      aria-label={t('optFamily')}
                      value={choice.family}
                      onChange={(event) =>
                        choose(value, {
                          family: event.target.value as ColorFamilyCode | '',
                          include: event.target.value !== '',
                        })
                      }
                      className="w-40"
                    >
                      <option value="">{t('optChooseFamily')}</option>
                      {COLOR_FAMILIES.map((entry) => (
                        <option key={entry.code} value={entry.code}>
                          {entry[locale]}
                        </option>
                      ))}
                    </Select>
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {skipped > 0 ? (
          <p data-numeric className="text-xs text-ink-muted">
            {t('optSuggestSkipped', { count: count(skipped, locale) })}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}

// ─── Small parts ─────────────────────────────────────────────────────────────

/** A family filter or choice: a rounded chip, filled when chosen. */
function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cx(
        'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
        active
          ? 'border-brand-600 bg-brand-50 text-brand-700'
          : 'border-line bg-surface text-ink-muted hover:bg-surface-sunken hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

/** A family's colour, beside its name. */
function Dot({ hex }: { hex: string | null }) {
  return (
    <span
      aria-hidden
      className={cx('inline-block size-3 shrink-0 rounded-full border border-line', !hex && 'border-dashed')}
      style={hex ? { backgroundColor: hex } : undefined}
    />
  );
}

/** A value's swatch — or, when it has none yet, the outline of one. */
function SwatchCircle({ hex, className }: { hex: string | null; className?: string }) {
  if (hex) {
    return (
      <span
        aria-hidden
        className={cx('shrink-0 rounded-full border border-line shadow-card', className)}
        style={{ backgroundColor: hex }}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-line text-ink-faint',
        className,
      )}
    >
      <SwatchIcon className="size-1/2" />
    </span>
  );
}

/** A switch with what it switches written beside it. */
function SwitchRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-control border border-line px-3 py-2.5">
      <span className="min-w-0">
        <span className="block text-sm text-ink">{label}</span>
        {hint ? <span className="block text-xs text-ink-muted">{hint}</span> : null}
      </span>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function optionName(option: CatalogOptionDto, locale: AdminLocale): string {
  return option.translations?.[locale] || option.name || option.code;
}

function valueName(value: CatalogOptionValueDto, locale: AdminLocale): string {
  return value.translations?.[locale] || value.name || value.code;
}

function familyName(code: ColorFamilyCode, locale: AdminLocale): string {
  return FAMILY_BY_CODE.get(code)?.[locale] ?? code;
}

/**
 * A code as the API makes one: lower-case Latin letters and digits, anything
 * else a dash. Shown as it will be saved, so nobody is surprised by `roll-10m`.
 */
function toCode(input: string, max: number): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max);
}

/** The same rule while typing, keeping a trailing dash so "roll-" can become "roll-10". */
function typingCode(input: string, max: number): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, max);
}

/** `#796727`, `796727` or `#abc` → `#796727`; null when it is not a colour. */
function normalizeHex(input: string): string | null {
  const raw = input.trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{3}$/.test(raw)) return `#${[...raw].map((digit) => digit + digit).join('')}`;
  return /^[0-9a-f]{6}$/.test(raw) ? `#${raw}` : null;
}
